"use client";

import { useEffect, useRef, useState } from "react";
import type {} from "altcha/types/react";

type AltchaFieldProps = {
  /** Receives the base64 payload once the proof-of-work is solved. */
  onVerified: (payload: string) => void;
};

// Reusable ALTCHA wrapper (login now, signup in P3). The web component
// cannot render during SSR, so its module is imported only in the browser
// and the widget mounts after that. auto="onload" solves the challenge
// immediately — the user never clicks anything.
export function AltchaField({ onVerified }: AltchaFieldProps) {
  const [ready, setReady] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([import("altcha"), import("altcha/i18n/pl")]).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = (event: Event) => {
      const payload = (event as CustomEvent<{ payload?: string }>).detail
        ?.payload;
      if (payload) onVerified(payload);
    };
    el.addEventListener("verified", handler);
    return () => el.removeEventListener("verified", handler);
  }, [ready, onVerified]);

  if (!ready) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Ładowanie weryfikacji…
      </p>
    );
  }

  return (
    <altcha-widget
      ref={ref}
      challenge="/api/captcha/challenge"
      auto="onload"
      language="pl"
    ></altcha-widget>
  );
}
